import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, Sparkles, AlertCircle, ShieldCheck, Edit3, EyeOff, Eye, Coins, Package, CheckCircle2, XCircle, Clock, Award } from "lucide-react";
import SustainabilityScore from "@/components/SustainabilityScore";
import AnalyticsTab from "@/components/AnalyticsTab";
import PendingPurchasesTab from "@/components/PendingPurchasesTab";
import FeedbackQueueTab from "@/components/FeedbackQueueTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const CATEGORIES = ["Beauty & Personal Care", "Home & Kitchen", "Fashion", "Electronics", "Food & Beverage", "Baby & Kids", "Fitness", "Office & Stationery"];
const MERCHANTS = [
  { name: "Amazon", logo: "https://logo.clearbit.com/amazon.in" },
  { name: "Flipkart", logo: "https://logo.clearbit.com/flipkart.com" },
  { name: "Myntra", logo: "https://logo.clearbit.com/myntra.com" },
  { name: "Nykaa", logo: "https://logo.clearbit.com/nykaa.com" },
];
const STATUSES = ["Active", "Draft", "Processing", "Needs Review", "Scoring Failed", "Delisted"];

const emptyForm = {
  name: "", brand: "", category: CATEGORIES[0], original_price: 799,
  description: "", images: [""], brand_url: "", merchant_product_url: "", merchant: "Amazon",
  merchant_logo: "https://logo.clearbit.com/amazon.in", certifications: "", featured_tags: "green-pick",
};

const StatCard = ({ icon, label, value, tone = "emerald", testId }) => {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    slate: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    red: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  };
  return (
    <div className="card-premium p-5" data-testid={testId}>
      <div className={`inline-grid place-items-center h-9 w-9 rounded-full ${tones[tone]}`}>{icon}</div>
      <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className="mono text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};

const AdminPage = () => {
  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [contests, setContests] = useState([]);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [brands, setBrands] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [overrideProduct, setOverrideProduct] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ new_score: 0, reason: "" });
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllPhrase, setDeleteAllPhrase] = useState("");
  const [deletingAll, setDeletingAll] = useState(false);
  const [rescoreState, setRescoreState] = useState(null);

  const fetchAll = async () => {
    const [pr, cr, sr, rr, br] = await Promise.all([
      api.get("/admin/products", { params: { status: statusFilter || undefined } }),
      api.get("/admin/contests").catch(() => ({ data: [] })),
      api.get("/admin/stats"),
      api.get("/admin/reward-rules"),
      api.get("/admin/brand-waitlist").catch(() => ({ data: [] })),
    ]);
    setProducts(pr.data); setContests(cr.data); setStats(sr.data); setRules(rr.data); setBrands(br.data);
  };

  useEffect(() => { if (user?.role === "admin") fetchAll(); /* eslint-disable-next-line */ }, [user, statusFilter]);

  // Hydrate rescore state from server once on mount so a page refresh during
  // a background rescore correctly shows the running state.
  useEffect(() => {
    if (user?.role !== "admin") return;
    (async () => {
      try {
        const { data } = await api.get("/admin/products/rescore-all/status");
        if (data) setRescoreState(data);
      } catch { /* silent */ }
    })();
  }, [user]);

  // Poll rescore state while it's running. Must be declared before any early return.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/products/rescore-all/status");
        if (alive) setRescoreState(data);
        if (alive && data?.running) setTimeout(tick, 4000);
      } catch { /* silent */ }
    };
    if (rescoreState?.running) tick();
    return () => { alive = false; };
  }, [rescoreState?.running]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login?next=/admin" replace />;
  if (user.role !== "admin") return (
    <div className="max-w-3xl mx-auto p-16 text-center">
      <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto" />
      <h1 className="text-3xl font-bold mt-4">Admin access required</h1>
      <p className="text-muted-foreground mt-2">Sign in as admin (admin@ecowallet.com) to manage the Ecowallet catalogue.</p>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const gallery = (form.images || []).map((s) => (s || "").trim()).filter(Boolean);
      const payload = {
        ...form,
        original_price: Number(form.original_price),
        certifications: (typeof form.certifications === "string" ? form.certifications.split(",") : form.certifications).map((s) => s.trim()).filter(Boolean),
        featured_tags: (typeof form.featured_tags === "string" ? form.featured_tags.split(",") : form.featured_tags).map((s) => s.trim()).filter(Boolean),
        gallery,
        image: gallery[0] || "",
      };
      delete payload.images;
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success(`${payload.name} updated`);
      } else {
        toast.info("Creating & running two-stage AI assessment... ~20-30s", { duration: 8000 });
        await api.post("/admin/products", payload);
        toast.success(`${payload.name} added & scored`);
      }
      setForm(emptyForm); setEditingId(null); setDialogOpen(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const rescore = async (id) => {
    setBusyId(id); toast.info("Re-running two-stage assessment...");
    try {
      const { data } = await api.post(`/admin/products/${id}/rescore`);
      toast.success(`New score: ${data.methodology.overall}/100 (${data.methodology.confidence} confidence)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Rescore failed"); }
    finally { setBusyId(null); }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/admin/products/${id}/status`, { status }); toast.success(`Status → ${status}`); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    const existingImages = (p.gallery && p.gallery.length > 0) ? p.gallery : (p.image ? [p.image] : [""]);
    setForm({
      name: p.name, brand: p.brand, category: p.category, original_price: p.original_price ?? p.price ?? 0,
      description: p.description || "",
      images: existingImages,
      brand_url: p.brand_url || "", merchant_product_url: p.merchant_product_url || "",
      merchant: p.merchant || "Amazon", merchant_logo: p.merchant_logo || "https://logo.clearbit.com/amazon.in",
      certifications: (p.certifications || []).join(", "), featured_tags: (p.featured_tags || []).join(", "),
    });
    setDialogOpen(true);
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? (Consider Delist instead)`)) return;
    await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); fetchAll();
  };

  const submitOverride = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/products/${overrideProduct.id}/override-score`, overrideForm);
      toast.success(`Score overridden to ${overrideForm.new_score} — audit logged`);
      setOverrideProduct(null); setOverrideForm({ new_score: 0, reason: "" }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteAll = async () => {
    if (deleteAllPhrase.trim() !== "DELETE ALL") return;
    setDeletingAll(true);
    try {
      const { data } = await api.post("/admin/products/delete-all", { confirm: deleteAllPhrase });
      toast.success(`Deleted ${data.deleted} products from the catalogue`);
      setDeleteAllOpen(false); setDeleteAllPhrase("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setDeletingAll(false); }
  };

  const rescoreAll = async () => {
    try {
      const { data } = await api.post("/admin/products/rescore-all", {});
      setRescoreState(data.state);
      if (data.already_running) toast("Rescore already running");
      else toast.success("Rescore-all started — this may take several minutes");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Admin Console</h1>
          <p className="text-muted-foreground mt-2">Manage products, run AI scoring, review contested & configure rewards.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={rescoreAll}
            disabled={products.length === 0 || rescoreState?.running}
            className="btn-pill bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="rescore-all-btn"
            title={rescoreState?.running ? `Rescoring… ${rescoreState.done}/${rescoreState.total}` : "Run the v2 intelligence pipeline on every product"}
          >
            <Sparkles className="h-4 w-4 mr-1" />
            {rescoreState?.running ? `Rescoring ${rescoreState.done}/${rescoreState.total}…` : "Rescore all products"}
          </button>
          <button
            onClick={() => { setDeleteAllPhrase(""); setDeleteAllOpen(true); }}
            disabled={products.length === 0}
            className="btn-pill bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900 hover:bg-red-100 dark:hover:bg-red-950/60 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="delete-all-btn"
            title={products.length === 0 ? "No products to delete" : `Delete all ${products.length} products`}
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete all products
          </button>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <button className="btn-pill bg-primary text-primary-foreground" data-testid="add-product-btn"><Plus className="h-4 w-4 mr-1" /> Add Product</button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit product" : "New product"}</DialogTitle>
              <DialogDescription>Fill product details. Adding a new product triggers automatic AI sustainability scoring.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4 mt-3" data-testid="admin-create-form">
              <FormInput label="Product name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required col={2} />
              <FormInput label="Brand *" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} required />
              <FormSelect label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} />
              <FormInput label="Original price ₹ (MRP) *" type="number" value={form.original_price} onChange={(v) => setForm({ ...form, original_price: v })} required col={2} placeholder="799" />
              <div className="col-span-2 rounded-2xl bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-900/40 px-4 py-3 text-xs text-emerald-800 dark:text-emerald-200" data-testid="price-auto-note">
                <div className="font-semibold">Everything else is auto-calculated</div>
                <div className="mt-0.5 opacity-90">
                  Sale price, cashback and GreenCoins are all derived from the AI sustainability score after this product is saved.
                  Cashback and GreenCoins are always equal and range from ₹50 (score 0) to ₹120 (score 100). The sale price is simply MRP minus that cashback.
                </div>
              </div>
              <FormSelect label="Merchant" value={form.merchant} onChange={(v) => { const m = MERCHANTS.find((x) => x.name === v); setForm({ ...form, merchant: v, merchant_logo: m.logo }); }} options={MERCHANTS.map((m) => m.name)} col={2} />
              <div className="col-span-2" data-testid="images-field">
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Product images (add up to 8)</label>
                <div className="mt-2 space-y-2">
                  {form.images.map((url, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`image-row-${idx}`}>
                      <span className="mono text-[10px] text-muted-foreground w-10">
                        {idx === 0 ? "MAIN" : `#${idx + 1}`}
                      </span>
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => {
                          const next = [...form.images];
                          next[idx] = e.target.value;
                          setForm({ ...form, images: next });
                        }}
                        placeholder="https://images.unsplash.com/..."
                        className="flex-1 rounded-2xl border border-border bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid={`image-input-${idx}`}
                      />
                      {url && (
                        <img src={url} alt="" className="h-10 w-10 rounded-xl object-cover border border-border" onError={(e) => (e.currentTarget.style.opacity = 0.3)} />
                      )}
                      {form.images.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, images: form.images.filter((_, i) => i !== idx) })}
                          className="text-muted-foreground hover:text-destructive p-2 rounded-full"
                          title="Remove image"
                          data-testid={`remove-image-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  {form.images.length < 8 && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, images: [...form.images, ""] })}
                      className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                      data-testid="add-image-btn"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add another image
                    </button>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">First image becomes the product thumbnail. Rest show in the gallery.</div>
              </div>
              <FormInput label="Brand website URL" value={form.brand_url} onChange={(v) => setForm({ ...form, brand_url: v })} col={2} placeholder="https://brand.com — used by AI extraction" />
              <FormInput label="Merchant product URL (affiliate)" value={form.merchant_product_url} onChange={(v) => setForm({ ...form, merchant_product_url: v })} col={2} placeholder="https://amazon.in/... — Buy Now uses this" />
              <FormInput label="Certifications (comma-sep)" value={form.certifications} onChange={(v) => setForm({ ...form, certifications: v })} col={2} placeholder="GOTS, USDA Organic, B Corp" />
              <FormInput label="Featured tags (comma-sep)" value={form.featured_tags} onChange={(v) => setForm({ ...form, featured_tags: v })} col={2} placeholder="green-pick, trending, most-sustainable" />
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <button type="submit" disabled={creating} className="col-span-2 btn-pill bg-primary text-primary-foreground disabled:opacity-50" data-testid="submit-product">
                {creating ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> {editingId ? "Saving..." : "Creating + AI scoring..."}</> : <><Sparkles className="h-4 w-4 mr-2" /> {editingId ? "Save changes" : "Create & Auto-Score with AI"}</>}
              </button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Delete-all confirmation dialog */}
      <Dialog open={deleteAllOpen} onOpenChange={(o) => { setDeleteAllOpen(o); if (!o) setDeleteAllPhrase(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 dark:text-red-300">Delete all products?</DialogTitle>
            <DialogDescription>
              This permanently removes <strong>all {products.length} products</strong> from the catalogue, along with related pending purchases and reward transactions. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 text-xs text-muted-foreground">
            Type <span className="mono font-bold text-red-700 dark:text-red-300">DELETE ALL</span> to confirm.
          </div>
          <input
            type="text"
            value={deleteAllPhrase}
            onChange={(e) => setDeleteAllPhrase(e.target.value)}
            placeholder="DELETE ALL"
            className="mt-2 w-full rounded-2xl border border-red-300 dark:border-red-800 bg-card px-4 py-3 text-sm mono focus:outline-none focus:ring-2 focus:ring-red-500"
            data-testid="delete-all-input"
            autoFocus
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setDeleteAllOpen(false)}
              className="text-xs px-4 py-2 rounded-full bg-muted"
              data-testid="delete-all-cancel"
            >Cancel</button>
            <button
              onClick={deleteAll}
              disabled={deleteAllPhrase.trim() !== "DELETE ALL" || deletingAll}
              className="text-xs px-4 py-2 rounded-full bg-red-600 text-white flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="delete-all-confirm"
            >
              {deletingAll ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Delete all
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Bento */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard icon={<Package className="h-4 w-4" />} label="Total Products" value={stats.total_products} testId="stat-total" />
          <StatCard icon={<CheckCircle2 className="h-4 w-4" />} label="Active" value={stats.active} tone="emerald" testId="stat-active" />
          <StatCard icon={<EyeOff className="h-4 w-4" />} label="Delisted" value={stats.delisted} tone="slate" testId="stat-delisted" />
          <StatCard icon={<Clock className="h-4 w-4" />} label="Processing" value={stats.processing} tone="amber" testId="stat-processing" />
          <StatCard icon={<XCircle className="h-4 w-4" />} label="Scoring Failed" value={stats.scoring_failed} tone="red" testId="stat-failed" />
          <StatCard icon={<AlertCircle className="h-4 w-4" />} label="Needs Review" value={stats.needs_review} tone="amber" testId="stat-review" />
          <StatCard icon={<Award className="h-4 w-4" />} label="Avg Score" value={`${stats.avg_score}/100`} tone="emerald" testId="stat-avg" />
          <StatCard icon={<Coins className="h-4 w-4" />} label="GreenCoins Issued" value={stats.total_greencoins.toLocaleString()} tone="amber" testId="stat-coins" />
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="mb-4">
          <TabsTrigger value="products" data-testid="tab-products">Products ({products.length})</TabsTrigger>
          <TabsTrigger value="contests" data-testid="tab-contests">Contests ({contests.length})</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Reward Rules ({rules.length})</TabsTrigger>
          <TabsTrigger value="brands" data-testid="tab-brands">Brand Onboarding ({brands.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending Rewards</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback Queue</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Customer Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button onClick={() => setStatusFilter("")} className={`text-xs px-3 py-1.5 rounded-full ${statusFilter === "" ? "bg-primary text-primary-foreground" : "bg-muted"}`} data-testid="filter-all">All</button>
            {STATUSES.map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full ${statusFilter === s ? "bg-primary text-primary-foreground" : "bg-muted"}`} data-testid={`filter-${s.toLowerCase().replace(/\s/g, "-")}`}>{s}</button>
            ))}
          </div>

          <div className="card-premium overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-6 py-3 border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
              <div className="col-span-4">Product</div>
              <div className="col-span-1 text-center">Score</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Price / Coins</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {products.map((p) => (
              <div key={p.id} className="grid grid-cols-12 gap-3 px-6 py-3 items-center border-b border-border/40 last:border-b-0 hover:bg-muted/30" data-testid={`admin-row-${p.id}`}>
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  <img src={p.image} alt="" className="h-10 w-10 rounded-xl object-cover" />
                  <div className="min-w-0">
                    <div className="font-semibold truncate text-sm">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{p.brand} · {p.category}</div>
                  </div>
                </div>
                <div className="col-span-1 flex justify-center">
                  {p.methodology && !p.methodology.error ? <SustainabilityScore score={p.sustainability_score} size={44} stroke={4} showLabel={false} /> : <span className="text-xs text-muted-foreground">—</span>}
                </div>
                <div className="col-span-2">
                  <StatusBadge status={p.status || "Active"} />
                </div>
                <div className="col-span-2 text-right">
                  <div className="mono text-sm font-bold">₹{p.price}</div>
                  {p.original_price > (p.price || 0) && (
                    <div className="mono text-[10px] text-muted-foreground">
                      <span className="line-through">₹{p.original_price}</span>
                      <span className="ml-1 text-emerald-600">−₹{p.discount_amount ?? (p.original_price - (p.price || 0))}</span>
                    </div>
                  )}
                  <div className="mono text-[11px] text-emerald-600">+{p.greencoins || p.green_points || 0} coins</div>
                </div>
                <div className="col-span-3 flex justify-end gap-1">
                  <IconBtn onClick={() => startEdit(p)} title="Edit" testId={`edit-${p.id}`}><Edit3 className="h-3.5 w-3.5" /></IconBtn>
                  <IconBtn onClick={() => rescore(p.id)} disabled={busyId === p.id} title="Re-score" testId={`rescore-${p.id}`}><RefreshCw className={`h-3.5 w-3.5 ${busyId === p.id ? "animate-spin" : ""}`} /></IconBtn>
                  <IconBtn onClick={() => setOverrideProduct(p)} title="Override score" testId={`override-${p.id}`}><Award className="h-3.5 w-3.5" /></IconBtn>
                  {(p.status || "Active") === "Delisted" ? (
                    <IconBtn onClick={() => changeStatus(p.id, "Active")} title="Re-list" testId={`relist-${p.id}`}><Eye className="h-3.5 w-3.5" /></IconBtn>
                  ) : (
                    <IconBtn onClick={() => changeStatus(p.id, "Delisted")} title="Delist" testId={`delist-${p.id}`}><EyeOff className="h-3.5 w-3.5" /></IconBtn>
                  )}
                  <IconBtn onClick={() => remove(p.id, p.name)} title="Delete" tone="red" testId={`delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="contests">
          <div className="space-y-3" data-testid="contests-list">
            {contests.length === 0 && (
              <div className="card-premium p-10 text-center text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto opacity-50" />
                <p className="mt-3">No contested scores yet.</p>
              </div>
            )}
            {contests.map((c) => (
              <div key={c.id} className="card-premium p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold">{c.product_name}</div>
                  <span className="mono text-xs text-muted-foreground">Score at contest: {c.score_at_contest}/100</span>
                </div>
                <p className="text-sm mt-2 text-foreground/80">"{c.reason}"</p>
                <div className="mt-3 text-xs text-muted-foreground">{c.email || "anon"} · {new Date(c.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="rules">
          <div className="space-y-3" data-testid="rules-list">
            {rules.map((r) => (
              <div key={r.id} className="card-premium p-5 grid grid-cols-2 sm:grid-cols-5 gap-3 items-center" data-testid={`rule-${r.id}`}>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Score band</div>
                  <div className="mono font-bold">{r.min_score}–{r.max_score}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Tier</div>
                  <div className="font-semibold">{r.label}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Coins / ₹100</div>
                  <div className="mono font-bold">{r.greencoins_per_100_rupees}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Max coins</div>
                  <div className="mono font-bold">{r.max_greencoins}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Bonus discount</div>
                  <div className="mono font-bold">+{r.discount_bonus_percent}%</div>
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground italic">These rules drive the GreenCoins shown on every product card and the reward tier automatically applied to purchases. Programmatic API to edit rules is live at PUT /api/admin/reward-rules/&#123;id&#125;.</p>
          </div>
        </TabsContent>

        <TabsContent value="brands">
          {brands.length === 0 && (
            <div className="card-premium p-10 text-center text-muted-foreground" data-testid="brands-empty">
              <AlertCircle className="h-8 w-8 mx-auto opacity-50" />
              <p className="mt-3">No brand waitlist entries yet. Share <a href="/for-brands" target="_blank" className="text-primary hover:underline">/for-brands</a> with sustainable brands.</p>
            </div>
          )}
          <div className="space-y-3" data-testid="brands-list">
            {brands.map((b) => (
              <BrandRow key={b.id} brand={b} onChanged={fetchAll} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab />
        </TabsContent>

        <TabsContent value="pending">
          <PendingPurchasesTab />
        </TabsContent>

        <TabsContent value="feedback">
          <FeedbackQueueTab />
        </TabsContent>

      </Tabs>

      {/* Score Override Dialog */}
      <Dialog open={!!overrideProduct} onOpenChange={(o) => !o && setOverrideProduct(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override AI score</DialogTitle>
            <DialogDescription>The original AI score will be preserved in the audit log. Only use this when you have strong evidence.</DialogDescription>
          </DialogHeader>
          {overrideProduct && (
            <form onSubmit={submitOverride} className="space-y-4" data-testid="override-form">
              <div className="text-sm"><strong>{overrideProduct.name}</strong> — current AI score: <span className="mono">{overrideProduct.sustainability_score}/100</span></div>
              <FormInput label="New score (0-100) *" type="number" value={overrideForm.new_score} onChange={(v) => setOverrideForm({ ...overrideForm, new_score: Number(v) })} required />
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Reason for override *</label>
                <textarea rows={4} required minLength={10} value={overrideForm.reason} onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" data-testid="override-reason" placeholder="Explain the evidence supporting this override..." />
              </div>
              <button type="submit" className="btn-pill w-full bg-primary text-primary-foreground" data-testid="override-submit">Save override & log audit</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


const BRAND_STATUSES = ["New", "Contacted", "Under Review", "Approved", "Onboarding", "Rejected"];

const BrandRow = ({ brand, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(brand.status);
  const [notes, setNotes] = useState("");
  const changeStatus = async (s) => {
    setStatus(s);
    try { await api.patch(`/admin/brand-waitlist/${brand.id}/status`, { status: s, notes: notes || undefined }); toast.success(`Status → ${s}`); setNotes(""); onChanged(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="card-premium p-5" data-testid={`brand-${brand.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold text-lg">{brand.brand_name}</div>
          <div className="text-xs text-muted-foreground">{brand.contact_name} · {brand.work_email} · {brand.phone}</div>
          <div className="text-xs text-muted-foreground">{brand.category} · {brand.product_count} products · {brand.location}</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => changeStatus(e.target.value)} className="text-xs rounded-full border border-border bg-card px-3 py-1.5" data-testid={`brand-status-${brand.id}`}>
            {BRAND_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary hover:underline" data-testid={`brand-expand-${brand.id}`}>{expanded ? "Hide" : "Details"}</button>
        </div>
      </div>
      {expanded && (
        <div className="mt-4 pt-4 border-t border-border grid sm:grid-cols-2 gap-4 text-sm">
          <div><strong>Website:</strong> {brand.website ? <a href={brand.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{brand.website}</a> : "—"}</div>
          <div><strong>Social:</strong> {brand.social_url || "—"}</div>
          <div className="sm:col-span-2"><strong>Description:</strong> <p className="mt-1 text-foreground/80">{brand.brand_description}</p></div>
          <div className="sm:col-span-2"><strong>Sustainability focus:</strong> <p className="mt-1 text-foreground/80">{brand.sustainability_focus}</p></div>
          <div><strong>Certifications:</strong> {brand.certifications || "—"}</div>
          <div><strong>Report URL:</strong> {brand.sustainability_report_url ? <a href={brand.sustainability_report_url} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{brand.sustainability_report_url}</a> : "—"}</div>
          <div><strong>Submitted:</strong> {new Date(brand.created_at).toLocaleString()}</div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Internal note (added on next status change)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-3 py-2 text-sm" placeholder="Add a private note..." />
          </div>
          {brand.internal_notes && brand.internal_notes.length > 0 && (
            <div className="sm:col-span-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">History</div>
              <ul className="mt-2 space-y-1 text-xs">
                {brand.internal_notes.map((n, i) => <li key={i}>· <span className="text-muted-foreground">{new Date(n.at).toLocaleDateString()} · {n.by}:</span> {n.note}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const StatusBadge = ({ status }) => {
  const map = {
    "Active": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    "Processing": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    "Draft": "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    "Delisted": "bg-slate-200 text-slate-500",
    "Scoring Failed": "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    "Needs Review": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  };
  return <span className={`inline-block text-[10px] uppercase tracking-widest font-semibold rounded-full px-2.5 py-1 ${map[status] || map.Active}`} data-testid={`status-${status.toLowerCase().replace(/\s/g, "-")}`}>{status}</span>;
};

const IconBtn = ({ children, onClick, title, disabled, tone, testId }) => (
  <button
    onClick={onClick} disabled={disabled} title={title} data-testid={testId}
    className={`p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-40 ${tone === "red" ? "hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600" : ""}`}
  >{children}</button>
);

const FormInput = ({ label, value, onChange, type = "text", required, col = 1, step, placeholder }) => (
  <div className={col === 2 ? "col-span-2" : ""}>
    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
    <input type={type} step={step} required={required} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
  </div>
);

const FormSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default AdminPage;
